import { type FC } from 'react';
import { Button, Group, NumberInput, Stack, Text } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useSplitPayoutOrder } from '~/entities/payout';
import { type PayoutTraderOrderV1SplitOrderResponse } from '~/generated/payoutBackend/model';
import { validateSplitBodyAmount } from './lib';

export interface SplitOrderProps {
    orderId: string;
    orderAmount?: string;
    minChildAmount?: string;
    onCancel?: VoidFunction;
    onSuccess?: (response: PayoutTraderOrderV1SplitOrderResponse) => void;
    onError?: (error: Error) => void;
}

export const SplitOrder: FC<SplitOrderProps> = ({
    orderId,
    orderAmount,
    minChildAmount,
    onCancel,
    onSuccess,
    onError,
}) => {
    const { mutate, isPending } = useSplitPayoutOrder({ onSuccess, onError });

    const form = useForm({
        initialValues: {
            bodyAmount: '' as string | number,
        },
        validate: {
            bodyAmount: (value) => {
                return validateSplitBodyAmount({
                    bodyAmount: value,
                    orderAmount,
                    minChildAmount,
                });
            },
        },
    });

    return (
        <form
            onSubmit={form.onSubmit((values) => {
                mutate({
                    orderId: {
                        value: orderId,
                    },
                    bodyAmount: {
                        value: values.bodyAmount.toString().replace(',', '.'),
                    },
                });
            })}
        >
            <Stack miw={300}>
                <Text
                    size='sm'
                    c='dimmed'
                >
                    Сумма ордера: {orderAmount ?? '-'}
                </Text>

                <NumberInput
                    label='Сумма первой части'
                    placeholder='Введите сумму'
                    min={0}
                    decimalScale={8}
                    allowNegative={false}
                    disabled={isPending}
                    {...form.getInputProps('bodyAmount')}
                />

                <Group justify='flex-end'>
                    <Button
                        title='Разделить ордер'
                        disabled={isPending}
                        loading={isPending}
                        type='submit'
                    >
                        Разделить
                    </Button>

                    {onCancel && (
                        <Button
                            title='Отмена'
                            color='red'
                            disabled={isPending}
                            type='button'
                            onClick={onCancel}
                        >
                            Отмена
                        </Button>
                    )}
                </Group>
            </Stack>
        </form>
    );
};
